import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Headline, TextButton, TextLink } from '@oneaudi/unified-web-components';
import { useSelector } from 'react-redux';
import {
  CpoLabelWrapper,
  DagLabel,
  ImageDiscalimer,
  ImageWrapper,
  NameAndTrimWrapper,
  OfferInfo,
  PriceWrapper,
  PriceDisclaimer,
  SuperScript,
  TagAndDagWrapper,
  TileWrapper,
  VehicleImage,
  VehicleInfo,
  WhyBuy,
  Price,
} from './VehicleTileCa.styles';
import { TagLabel } from '../../Common/Common.styles';
import { PromotionType } from '../../../types/offers';
import CpoLabel from '../../../assets/icons/CpoLabel';
import PricingOptionsCa from './PricingOptionsCa';
import { CTAsWrapper, InventoryCTA, MainCta } from '../VehicleTile/VehicleTile.styles';
import { StyledTextButton } from '../../FeaturedOffers/ButtonGroups/ButtonGroups.styles';
import useCtaUrls from '../../../hooks/useCtaUrls';
import { numberFormatter } from '../../../utils/formatPrice';
import Modal from '../../Modal';
import { selectDealerContextId } from '../../../reducers/dealerContextReducer';
import { parseDigitalLeadsData } from '../../../utils/digitalLeadsFormat';
import { selectZipCode } from '../../../reducers/zipCodeReducer';

interface VehicleTileCaProps {
  offer: PromotionType;
}

const VehicleTileCa: React.FC<VehicleTileCaProps> = ({ offer }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isFrenchPage = lang === 'fr';

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { dealerId } = useSelector(selectDealerContextId);

  const { market } = useSelector(selectZipCode);

  const {
    offerType,
    offerModelDetail,
    offerImageUrl,
    term,
    programToDate,
    customerDownPayment,
    monthlyPayment,
    aprRate,
  } = offer;

  const {
    dag,
    vehicleType,
    modelYear,
    modelName,
    buildAudiUrlEn,
    buildAudiUrlFr,
    msrp,
    trimline,
    modelId,
    bodyType,
  } = offerModelDetail;

  const inventoryUrl = useCtaUrls(offer);

  const isCpoOffer = vehicleType === 'USED-CAR';
  const isCaMarket = market === 'CA';

  const buildYourAudiUrl = isFrenchPage ? buildAudiUrlFr : buildAudiUrlEn;

  const digitalLeadsInfo = useMemo(() => {
    // Passing empty string because fro CA we don't need ZipCode
    const dataInfo = parseDigitalLeadsData(offer, isCaMarket, '', dealerId);
    return dataInfo;
  }, [offer, dealerId, isCaMarket]);

  const disabledCta = !isCpoOffer && !buildYourAudiUrl;

  const href = useMemo(() => {
    // eslint-disable-next-line no-script-url
    if (disabledCta) return 'javascript:void(0)';
    if (isCpoOffer) return `https://www.audi.ca/${lang}/cpo/layer/Why-buy-certified/`;
    return buildYourAudiUrl;
  }, [buildYourAudiUrl, disabledCta, isCpoOffer, lang]);

  return (
    <>
      <TileWrapper
        isCpoOffer={isCpoOffer}
        data-promotion-type={offerType} // either lease, finane, or credit
        data-promotion-offerdate={programToDate} // validity date of offer
        data-promotion-credit={offer.creditBonus} // credit bonus offer // ??
        data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'} // downpayment for lease offer
        data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'} // monthly payment for lease offer
        data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'} // term length for lease offer
        data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'} // apr for finance offer
        data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'} // termlength for finance offer
        data-vehicle-model-name={modelName} // name of model -excludes year // ?
        data-vehicle-model-year={modelYear} // year of model
        data-vehicle-price={msrp !== 0 ? msrp : 'null'} // price of vehicle // ?
        data-vehicle-trim={trimline} // vehicle trim
        data-testid={`offer-tile-${vehicleType}-${dag}`}
      >
        <TagAndDagWrapper>
          <TagLabel data-testid={`${offerType}-tag`}>
            {t(`promo.tile.offerType.${offerType}`)}
          </TagLabel>
          <DagLabel>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t(`promo.tile.dag.${dag}`)}
            </Text>
          </DagLabel>
        </TagAndDagWrapper>
        <ImageWrapper isCpoOffer={isCpoOffer}>
          <VehicleImage
            data-testid="offer-hero-image"
            src={offerImageUrl}
            alt="vehicle-sample-img"
          />
          {isCpoOffer && <ImageDiscalimer>{t('promo.tile.imageReference')}</ImageDiscalimer>}
        </ImageWrapper>
        {isCpoOffer && (
          <CpoLabelWrapper>
            <CpoLabel />
          </CpoLabelWrapper>
        )}
        <NameAndTrimWrapper>
          <Headline
            data-testid="offer-name"
            tag="h3"
            variant={{
              order: '3',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {modelName}
          </Headline>
          {!isCpoOffer && (
            <Text
              variant={{
                order: '1',
                style: 'normal',
              }}
            >
              {trimline}
            </Text>
          )}
        </NameAndTrimWrapper>
        {!isCpoOffer && (
          <PriceWrapper>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t(`promo.tile.startingAt`)}
            </Text>
            <Price>
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {numberFormatter(msrp, lang)}
              </Headline>
              <SuperScript>
                <TextLink
                  className="link"
                  onClick={() => setIsModalOpen(!isModalOpen)}
                  size="sm"
                  variant="inline"
                >
                  1
                </TextLink>
              </SuperScript>
            </Price>
          </PriceWrapper>
        )}
        <VehicleInfo>
          <OfferInfo isCpoOffer={isCpoOffer}>
            <PricingOptionsCa offer={offer} showOfferDetails={() => setIsModalOpen(true)} />
          </OfferInfo>
        </VehicleInfo>
        <CTAsWrapper>
          <MainCta id="digital-leads" data-info={digitalLeadsInfo} data-testid="offer-contact-cta">
            {t('promo.offers.cta.contact')}
          </MainCta>
          <InventoryCTA
            data-testid="offer-inventory-cta"
            variant="secondary"
            href={inventoryUrl}
            iconPosition="trailing"
            icon="external-link"
            target="_blank"
            isFullWidth
          >
            {t('promo.offers.cta.inventory')}
          </InventoryCTA>
        </CTAsWrapper>
        {isCpoOffer && <PriceDisclaimer>{t('promo.tile.priceDisclaimer')}</PriceDisclaimer>}
        <WhyBuy disabledCta={disabledCta}>
          {/* @ts-ignore */}
          <StyledTextButton
            data-testid="why-buy-cpo-cta"
            type="button"
            href={href}
            target="_blank"
            onClick={() => {}}
            variant="tertiary"
            icon="external-link"
            iconPosition="trailing"
            isFullWidth
          >
            {isCpoOffer ? t('promo.tile.whyBuyCpo') : t('promo.tile.buildAudi')}
          </StyledTextButton>
        </WhyBuy>
      </TileWrapper>
      {isModalOpen && (
        <Modal
          offer={offer}
          inventoryUrl={inventoryUrl}
          onCloseModal={() => setIsModalOpen(false)}
          dealerId={dealerId}
          isModal
        />
      )}
    </>
  );
};

export default VehicleTileCa;
